import chemAnchor from "@/assets/objectives/rewards/chem-anchor.webp?no-inline";
import chemDeadeye from "@/assets/objectives/rewards/chem-deadeye.webp?no-inline";
import chemHawk from "@/assets/objectives/rewards/chem-hawk.webp?no-inline";
import chemHunter from "@/assets/objectives/rewards/chem-hunter.webp?no-inline";
import chemMaestro from "@/assets/objectives/rewards/chem-maestro.webp?no-inline";
import chemSentinel from "@/assets/objectives/rewards/chem-sentinel.webp?no-inline";
import chemShadow from "@/assets/objectives/rewards/chem-shadow.webp?no-inline";
import richmond1 from "@/assets/objectives/rewards/cosmetic-richmond-1.webp?no-inline";
import richmond2 from "@/assets/objectives/rewards/cosmetic-richmond-2.webp?no-inline";
import richmond3 from "@/assets/objectives/rewards/cosmetic-richmond-3.webp?no-inline";
import evolution from "@/assets/objectives/rewards/evolution.webp?no-inline";
import packBlue from "@/assets/objectives/rewards/pack-blue.webp?no-inline";
import packBronze from "@/assets/objectives/rewards/pack-bronze.webp?no-inline";
import packGold from "@/assets/objectives/rewards/pack-gold.webp?no-inline";
import packSilver from "@/assets/objectives/rewards/pack-silver.webp?no-inline";
import pick from "@/assets/objectives/rewards/pick.webp?no-inline";
import lindelof from "@/assets/objectives/rewards/player-lindelof.webp?no-inline";
import rosa from "@/assets/objectives/rewards/player-rosa.webp?no-inline";
import savinho from "@/assets/objectives/rewards/player-savinho.webp?no-inline";
import season from "@/assets/objectives/rewards/season-points.webp?no-inline";

const coins = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/fut-coin-large.png`;

/*
  What each sample plan pays out, grouped by kind. The page calls these "real
  examples", but for now only the 15-game plan is transcribed from a real plan
  screen (a tile's "N×" counts as N, "Partial" rewards and tiles whose art
  failed to load are left out). Each shorter plan is cut down from the one
  above it, so a longer plan always earns everything the shorter one does and
  more. Replace the 5- and 10-game plans with exported real plans before
  launch. A plan's total is the sum of its items.

  The art is cut from those screens too. Cosmetics only had clean art for the
  three AFC Richmond items, so that set repeats to fill the stack.
*/

export type RewardGroupId =
  | "packsAndPicks"
  | "players"
  | "evolutions"
  | "coins"
  | "seasonPoints"
  | "chemStyles"
  | "cosmetics";

export type RewardGroup = {
  id: RewardGroupId;
  /* One image per reward, in stacking order: the last is drawn on top. */
  items: string[];
  /* The headline number when it is not the item count, e.g. total coins. */
  value?: number;
};

export type RewardPlan = {
  games: 5 | 10 | 15;
  groups: RewardGroup[];
};

const times = (n: number, src: string) => Array<string>(n).fill(src);

const cosmetics = (n: number) =>
  Array.from({ length: n }, (_, i) => [richmond1, richmond2, richmond3][i % 3]);

// Picks and the cheaper packs sit underneath; the blue pack ends on top.
const packsAndPicks = (p: {
  picks: number;
  bronze: number;
  silver: number;
  gold: number;
  blue: number;
}) => [
  ...times(p.picks, pick),
  ...times(p.bronze, packBronze),
  ...times(p.silver, packSilver),
  ...times(p.gold, packGold),
  ...times(p.blue, packBlue),
];

// The 15-game screen's chem styles, in its order; shorter plans take a prefix.
const CHEM_15 = [
  chemAnchor,
  chemAnchor,
  chemHunter,
  chemShadow,
  chemSentinel,
  chemDeadeye,
  chemMaestro,
  chemHawk,
];

export const REWARD_PLANS: RewardPlan[] = [
  {
    games: 5,
    groups: [
      {
        id: "packsAndPicks",
        items: packsAndPicks({
          picks: 1,
          bronze: 1,
          silver: 1,
          gold: 1,
          blue: 6,
        }),
      },
      { id: "players", items: [lindelof] },
      { id: "evolutions", items: [evolution] },
      { id: "coins", items: [coins], value: 5500 },
      { id: "seasonPoints", items: [season], value: 180 },
      { id: "chemStyles", items: CHEM_15.slice(0, 3) },
      { id: "cosmetics", items: cosmetics(3) },
    ],
  },
  {
    games: 10,
    groups: [
      {
        id: "packsAndPicks",
        items: packsAndPicks({
          picks: 2,
          bronze: 1,
          silver: 1,
          gold: 1,
          blue: 14,
        }),
      },
      { id: "players", items: [lindelof, rosa] },
      { id: "evolutions", items: [evolution] },
      { id: "coins", items: [coins], value: 15100 },
      { id: "seasonPoints", items: [season], value: 280 },
      { id: "chemStyles", items: CHEM_15.slice(0, 5) },
      { id: "cosmetics", items: cosmetics(5) },
    ],
  },
  {
    games: 15,
    groups: [
      {
        id: "packsAndPicks",
        items: packsAndPicks({
          picks: 3,
          bronze: 2,
          silver: 1,
          gold: 2,
          blue: 20,
        }),
      },
      { id: "players", items: [lindelof, rosa, savinho] },
      { id: "evolutions", items: times(2, evolution) },
      { id: "coins", items: [coins], value: 25600 },
      { id: "seasonPoints", items: [season], value: 580 },
      { id: "chemStyles", items: CHEM_15 },
      { id: "cosmetics", items: cosmetics(7) },
    ],
  },
];
